import * as React from 'react';
import {
  PauseIcon,
  PlayIcon,
  SkipBackIcon,
  ClockIcon,
  RadioIcon,
  CircleDotIcon,
  CheckCircle2Icon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  CONFERENCE_DAY_END_MINUTES,
  CONFERENCE_DAY_START_MINUTES,
  CONFERENCE_TIMELINE_STEP_MINUTES,
  formatScheduleTime12,
  formatScheduleTime24,
  snapMinutes,
  type ScheduleStatus,
  type TimeBasedHuntResult,
} from '@/hooks/use-time-based-hunt';

const DEFAULT_PLAY_INTERVAL_MS = 700;

export interface ScheduleTimelineSliderProps {
  /** Selected event-clock minutes from midnight. */
  value: number;
  onChange: (minutes: number) => void;
  /** Session start/end markers in minutes (from useTimeBasedHunt). */
  markers?: number[];
  /** Minutes where the participant completed a session challenge (checked ticks). */
  completedMarkers?: number[];
  /** Optional live/upcoming/ended counts for status badges. */
  counts?: TimeBasedHuntResult['counts'];
  /** Optional live room names shown under the scrubber. */
  liveRoomNames?: string[];
  /** Room labels for completed session quizzes. */
  completedRoomNames?: string[];
  min?: number;
  max?: number;
  step?: number;
  /** Auto-advance interval while playing. */
  playIntervalMs?: number;
  className?: string;
  /** Compact layout for overlay toolbars (e.g. 3D twin). */
  compact?: boolean;
  /** Render only the event time and playback controls. */
  minimized?: boolean;
  disabled?: boolean;
}

const STATUS_BADGE_STYLES: Record<
  Exclude<ScheduleStatus, 'unscheduled'>,
  { label: string; className: string; icon: typeof RadioIcon }
> = {
  live: {
    label: 'Live',
    className:
      'border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    icon: RadioIcon,
  },
  upcoming: {
    label: 'Upcoming',
    className: 'border-transparent bg-sky-500/15 text-sky-700 dark:text-sky-400',
    icon: CircleDotIcon,
  },
  ended: {
    label: 'Ended',
    className: 'border-transparent bg-muted text-muted-foreground',
    icon: CheckCircle2Icon,
  },
};

export function ScheduleStatusBadge({
  status,
  count,
  className,
}: {
  status: Exclude<ScheduleStatus, 'unscheduled'>;
  count?: number;
  className?: string;
}) {
  const meta = STATUS_BADGE_STYLES[status];
  const Icon = meta.icon;
  return (
    <Badge
      variant="outline"
      className={cn(meta.className, 'gap-1 tabular-nums', className)}
      data-status={status}
    >
      <Icon className="size-3" aria-hidden />
      {meta.label}
      {typeof count === 'number' ? (
        <span className="font-semibold">{count}</span>
      ) : null}
    </Badge>
  );
}

export function ScheduleTimelineSlider({
  value,
  onChange,
  markers = [],
  completedMarkers = [],
  counts,
  liveRoomNames = [],
  completedRoomNames = [],
  min = CONFERENCE_DAY_START_MINUTES,
  max = CONFERENCE_DAY_END_MINUTES,
  step = CONFERENCE_TIMELINE_STEP_MINUTES,
  playIntervalMs = DEFAULT_PLAY_INTERVAL_MS,
  className,
  compact = false,
  minimized = false,
  disabled = false,
}: ScheduleTimelineSliderProps) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef(false);
  const [playing, setPlaying] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);

  const range = Math.max(1, max - min);
  const clampedValue = snapMinutes(value, step, min, max);
  const percent = ((clampedValue - min) / range) * 100;

  const minutesFromClientX = React.useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return clampedValue;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return clampedValue;
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return snapMinutes(min + ratio * range, step, min, max);
    },
    [clampedValue, min, max, range, step],
  );

  const commit = React.useCallback(
    (next: number) => {
      const snapped = snapMinutes(next, step, min, max);
      if (snapped !== clampedValue) onChange(snapped);
    },
    [clampedValue, max, min, onChange, step],
  );

  React.useEffect(() => {
    if (!playing || disabled) return;
    const id = window.setInterval(() => {
      const next = clampedValue + step;
      if (next > max) {
        setPlaying(false);
        commit(max);
        return;
      }
      commit(next);
    }, playIntervalMs);
    return () => window.clearInterval(id);
  }, [playing, disabled, clampedValue, step, max, playIntervalMs, commit]);

  React.useEffect(() => {
    if (disabled) setPlaying(false);
  }, [disabled]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);
    setPlaying(false);
    event.currentTarget.setPointerCapture(event.pointerId);
    commit(minutesFromClientX(event.clientX));
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || disabled) return;
    commit(minutesFromClientX(event.clientX));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore if already released
    }
  };

  const hourTicks = React.useMemo(() => {
    const ticks: number[] = [];
    // Label on the hour when possible; always include day bounds.
    const firstHour = Math.ceil(min / 60) * 60;
    for (let t = firstHour; t <= max; t += 60) {
      if (t >= min && t <= max) ticks.push(t);
    }
    if (!ticks.includes(min)) ticks.unshift(min);
    if (!ticks.includes(max)) ticks.push(max);
    return ticks;
  }, [min, max]);

  const uniqueMarkers = React.useMemo(() => {
    const set = new Set<number>();
    for (const m of markers) {
      if (m >= min && m <= max) set.add(snapMinutes(m, step, min, max));
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [markers, min, max, step]);

  const completedMarkerSet = React.useMemo(() => {
    const set = new Set<number>();
    for (const m of completedMarkers) {
      if (m >= min && m <= max) set.add(snapMinutes(m, step, min, max));
    }
    return set;
  }, [completedMarkers, min, max, step]);

  const playbackControls = (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="size-8"
        disabled={disabled}
        aria-label="Reset to start of day"
        onClick={() => {
          setPlaying(false);
          commit(min);
        }}
      >
        <SkipBackIcon className="size-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant={playing ? 'default' : 'outline'}
        className="size-8"
        disabled={disabled}
        aria-label={playing ? 'Pause timeline preview' : 'Play timeline preview'}
        onClick={() => setPlaying((p) => !p)}
      >
        {playing ? <PauseIcon className="size-4" /> : <PlayIcon className="size-4" />}
      </Button>
    </div>
  );

  if (minimized) {
    return (
      <div className={cn('flex min-w-0 items-center justify-between gap-1.5', className)}>
        <div className="flex min-w-0 items-center gap-2">
          <ClockIcon className="size-4 shrink-0 text-primary" aria-hidden />
          <p className="truncate text-sm font-semibold tabular-nums">
            {formatScheduleTime12(clampedValue)}
          </p>
        </div>
        {playbackControls}
      </div>
    );
  }

  const controls = (
    <div className={cn('space-y-3', compact && 'space-y-2.5')}>
      <div className={cn('flex flex-wrap items-center justify-between gap-2', compact && 'gap-1.5')}>
        <div className="flex items-center gap-2">
          <ClockIcon className="size-4 text-primary" aria-hidden />
          <div className="leading-tight">
            <p className="text-sm font-semibold tabular-nums">
              {formatScheduleTime12(clampedValue)}
            </p>
            {!compact && (
              <p className="text-[11px] text-muted-foreground tabular-nums">
                Event clock {formatScheduleTime24(clampedValue)}
              </p>
            )}
          </div>
        </div>

        {playbackControls}
      </div>

      {counts ? (
        <div className="flex flex-wrap gap-1" aria-label="Session status summary">
          <ScheduleStatusBadge status="live" count={counts.live} />
          <ScheduleStatusBadge status="upcoming" count={counts.upcoming} />
          <ScheduleStatusBadge status="ended" count={counts.ended} />
        </div>
      ) : null}

      <div className="space-y-1.5">
        <div
          ref={trackRef}
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={clampedValue}
          aria-valuetext={formatScheduleTime12(clampedValue)}
          aria-label="Conference schedule timeline"
          aria-disabled={disabled || undefined}
          className={cn(
            'relative h-10 touch-none select-none rounded-lg border border-border bg-muted/40 px-1',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
            isDragging && 'ring-2 ring-primary/40',
          )}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
              event.preventDefault();
              setPlaying(false);
              commit(clampedValue - step);
            } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
              event.preventDefault();
              setPlaying(false);
              commit(clampedValue + step);
            } else if (event.key === 'Home') {
              event.preventDefault();
              setPlaying(false);
              commit(min);
            } else if (event.key === 'End') {
              event.preventDefault();
              setPlaying(false);
              commit(max);
            }
          }}
        >
          <div
            className="pointer-events-none absolute inset-y-1 left-1 rounded-md bg-primary/15"
            style={{ width: `calc(${percent}% - 0.25rem)` }}
          />

          {uniqueMarkers.map((m) => {
            const left = ((m - min) / range) * 100;
            const isCompleted = completedMarkerSet.has(m);
            return (
              <span
                key={m}
                title={`${formatScheduleTime12(m)}${isCompleted ? ' · completed' : ''}`}
                className={
                  isCompleted
                    ? 'pointer-events-none absolute top-1 flex size-3 -translate-x-1/2 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm'
                    : 'pointer-events-none absolute top-1.5 bottom-1.5 w-0.5 -translate-x-1/2 rounded-full bg-amber-500/80'
                }
                style={{ left: `${left}%` }}
                aria-label={
                  isCompleted
                    ? `Completed session at ${formatScheduleTime12(m)}`
                    : `Session marker at ${formatScheduleTime12(m)}`
                }
              >
                {isCompleted ? <CheckCircle2Icon className="size-2.5" aria-hidden /> : null}
              </span>
            );
          })}

          <div
            className={cn(
              'pointer-events-none absolute top-1/2 z-10 flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-primary bg-background shadow-md',
              isDragging && 'scale-110',
            )}
            style={{ left: `${percent}%` }}
          >
            <span className="size-1.5 rounded-full bg-primary" />
          </div>
        </div>

        <div className="relative h-4 text-[10px] text-muted-foreground tabular-nums">
          {hourTicks.map((t) => {
            const left = ((t - min) / range) * 100;
            const align =
              t === min ? 'translate-x-0' : t === max ? '-translate-x-full' : '-translate-x-1/2';
            return (
              <span
                key={t}
                className={cn('absolute top-0', align)}
                style={{ left: `${left}%` }}
              >
                {formatScheduleTime12(t)}
              </span>
            );
          })}
        </div>
      </div>

      {(liveRoomNames.length > 0 || completedRoomNames.length > 0) ? (
        <div className="space-y-1.5">
          {liveRoomNames.length > 0 && (
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <span className="shrink-0 text-[11px] font-medium text-muted-foreground">Live rooms:</span>
              {liveRoomNames.map((room) => (
                <Badge
                  key={room}
                  variant="outline"
                  className="max-w-full truncate border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 dark:text-emerald-400"
                >
                  {room}
                </Badge>
              ))}
            </div>
          )}
          {completedRoomNames.length > 0 && (
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <span className="shrink-0 text-[11px] font-medium text-muted-foreground">Completed:</span>
              {completedRoomNames.map((room) => (
                <Badge
                  key={`done-${room}`}
                  variant="outline"
                  className="max-w-full gap-1 truncate border-emerald-600/40 bg-emerald-600/10 text-[10px] text-emerald-800 dark:text-emerald-300"
                >
                  <CheckCircle2Icon className="size-2.5" aria-hidden />
                  {room}
                </Badge>
              ))}
            </div>
          )}
        </div>
      ) : (
        !compact && (
          <p className="text-[11px] text-muted-foreground">
            Drag the timeline to preview which sessions and hunt items are active.
          </p>
        )
      )}
    </div>
  );

  if (compact) {
    return <div className={className}>{controls}</div>;
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClockIcon className="size-4 text-primary" />
          Conference Timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">{controls}</CardContent>
    </Card>
  );
}
